/** Move a support ticket on, with a reason (`console_support_tickets_manage`).
 *
 *  Forward only — `open → acknowledged → resolved → closed`, a step may be skipped — so the choices
 *  are the ticket's `next_statuses`. The reason stays with us: the ticket's status history and the
 *  console audit log keep it, the client's Activity shows only the two statuses. When the ticket
 *  moved meanwhile, the backend refuses the move (409 `status_refused`): the dialog says so and
 *  reloads the ticket, so the next try starts from where it stands. */
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import {
  changeStatus,
  statusChangeErrorText,
  statusLabel,
  statusRefusedOf,
  SUPPORT_TICKET_REASON_MAX,
  supportTicketKeys,
  type SupportTicketDetail,
  type SupportTicketStatus,
} from '@/api/supportTickets';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';

const FormSchema = z.object({
  status: z.string().min(1, 'Pick the status to move to.'),
  reason: z
    .string()
    .trim()
    .min(1, 'Say why — it is kept on the ticket’s history.')
    .max(SUPPORT_TICKET_REASON_MAX, `At most ${SUPPORT_TICKET_REASON_MAX} characters.`),
});
type FormValues = z.infer<typeof FormSchema>;

export function StatusChangeDialog({
  ticket,
  queryKey,
  onClose,
}: {
  ticket: SupportTicketDetail;
  /** The page's detail query: the answer replaces it. */
  queryKey: readonly unknown[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { status: ticket.next_statuses[0] ?? '', reason: '' },
  });

  // A refusal reloads the ticket: a picked status it can no longer move to gives way to its next
  // one (none once it is closed); the reason typed so far stays.
  const picked = useWatch({ control, name: 'status' });
  const choices = ticket.next_statuses;
  useEffect(() => {
    if (!choices.includes(picked as SupportTicketStatus)) setValue('status', choices[0] ?? '');
  }, [choices, picked, setValue]);

  const mutation = useMutation({
    mutationFn: (v: FormValues) =>
      changeStatus(ticket.public_id, {
        status: v.status as SupportTicketStatus,
        reason: v.reason.trim(),
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(queryKey, next);
      void queryClient.invalidateQueries({ queryKey: supportTicketKeys.list });
      snackbar.show(
        `${next.public_id} is ${statusLabel(next.status).toLowerCase()} now.`,
        'success',
      );
      onClose();
    },
    onError: (e) => {
      // Moved meanwhile: show where it stands now.
      if (statusRefusedOf(e)) void queryClient.invalidateQueries({ queryKey });
    },
  });
  const refused = statusRefusedOf(mutation.error);

  return (
    <AppDialog
      open
      onClose={onClose}
      size="sm"
      disableBackdropClose
      title={`Move ${ticket.public_id} on`}
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={mutation.isPending || choices.length === 0}
            onClick={handleSubmit((v) => mutation.mutate(v))}
          >
            {mutation.isPending ? 'Saving…' : 'Change status'}
          </Button>
        </>
      }
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Now {statusLabel(ticket.status).toLowerCase()}. A ticket only moves forward; the client’s
          Activity shows the two statuses, never the reason or who moved it.
        </Typography>
        <Controller
          control={control}
          name="status"
          render={({ field }) => (
            <TextField
              select
              size="small"
              label="New status"
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              error={Boolean(errors.status)}
              helperText={errors.status?.message}
            >
              {choices.map((s) => (
                <MenuItem key={s} value={s}>
                  {statusLabel(s)}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <TextField
          {...register('reason')}
          size="small"
          label="Reason"
          required
          multiline
          minRows={2}
          inputProps={{ maxLength: SUPPORT_TICKET_REASON_MAX }}
          error={Boolean(errors.reason)}
          helperText={
            errors.reason?.message ??
            'Kept on the ticket’s status history and in the console audit log; never shown to the client.'
          }
        />
        {mutation.isError &&
          (refused ? (
            <Alert severity="warning" data-status-refused>
              {refused.detail} The ticket was reloaded — check where it stands now.
            </Alert>
          ) : (
            <Alert severity="error">{statusChangeErrorText(mutation.error)}</Alert>
          ))}
      </Stack>
    </AppDialog>
  );
}
